import { useEffect, useState } from 'react';

import { styled } from '@mui/material';
import { Typography } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IllustStatusBlank } from '@kep/kcds-icons';

import TaskItemCard from './TaskItemCard';

import type { Task } from '@kc-console/context';

interface TaskListProps {
  onDismiss: (taskId: string) => void;
  tasks: Task[];
}

interface DisplayItem {
  entering: boolean;
  leaving: boolean;
  task: Task;
}

const TaskList = ({ tasks, onDismiss }: TaskListProps) => {
  const { t } = useTranslation();
  // 최초 목록은 enter 애니메이션 없이(entering: false) 그대로 보여준다.
  const [items, setItems] = useState<DisplayItem[]>(() =>
    tasks.map(task => ({ task, entering: false, leaving: false })),
  );

  // tasks가 바뀌면 표시 목록을 다시 맞춘다. enter/leaving을 여기서 같이 결정한다.
  useEffect(() => {
    setItems(prev => {
      const aliveIds = new Set(tasks.map(task => task.id));

      // 1) 서버가 준 순서(updatedAt:desc) 그대로 표시한다. FE에서 순서를 바꾸지 않는다.
      //    직전 목록에 없던 task만 entering으로 등장 애니메이션을 붙인다.
      const next: DisplayItem[] = tasks.map(task => ({
        task,
        entering: !prev.some(item => item.task.id === task.id),
        leaving: false,
      }));

      // 2) 서버에서 사라진 카드는 제자리에서 collapse 되도록 이전 위치에 잠깐 남긴다.
      prev.forEach((item, index) => {
        if (!aliveIds.has(item.task.id)) {
          next.splice(index, 0, { task: item.task, entering: false, leaving: true });
        }
      });

      return next;
    });
  }, [tasks]);

  const handleLeaveEnd = (taskId: string) => {
    setItems(prev => prev.filter(item => item.task.id !== taskId));
  };

  if (items.length === 0) {
    return (
      <EmptyState>
        <EmptyIllust>
          <IllustStatusBlank />
        </EmptyIllust>
        <Typography variant="label/medium/lg" color="text/weakest/default">
          {t('layout:task_manager.list.empty.description')}
        </Typography>
      </EmptyState>
    );
  }

  return (
    <>
      {items.map(({ task, entering, leaving }) => (
        <TaskItemCard
          key={task.id}
          task={task}
          onDismiss={onDismiss}
          entering={entering}
          leaving={leaving}
          onLeaveEnd={handleLeaveEnd}
        />
      ))}
    </>
  );
};

export default TaskList;

const EmptyState = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 100%;
`;

/** SVG 자체에 width/height 어트리뷰트가 없어 CSS로 크기를 강제(notification과 동일). */
const EmptyIllust = styled('div')`
  width: 72px;
  height: 72px;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;
